import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Play, Loader2, Video, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicVideoPage() {
  const { publicCode } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itsId, setItsId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/api/videos/public/${publicCode}`);
        setVideo(response.data);
        setError(null);
      } catch (error) {
        if (error.response?.status === 404) {
          setError('Video not found or has been deactivated');
        } else {
          setError('Failed to load video');
        }
      } finally {
        setLoading(false);
      }
    };

    if (publicCode) {
      fetchVideo();
    }
  }, [publicCode]);

  const handleItsChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setItsId(value);
    
    // Auto-submit when 8 digits are entered
    if (value.length === 8) {
      handleSubmit(value);
    }
  };

  const handleSubmit = async (itsValue = itsId) => {
    if (itsValue.length !== 8) {
      toast.error('Please enter a valid 8-digit ITS ID');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_BASE}/api/videos/public/${publicCode}/watch`, {
        its_id: itsValue
      });
      
      setRedirecting(true);
      toast.success(`Welcome ${response.data.member_name}! Redirecting to video...`);
      
      // Small delay for UX then redirect
      setTimeout(() => {
        window.location.href = response.data.youtube_url;
      }, 1500);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to verify ITS ID';
      toast.error(errorMsg);
      setItsId('');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Video Unavailable</h2>
            <p className="text-slate-300">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="relative">
              <Play className="w-20 h-20 mx-auto text-red-500 mb-4" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 border-4 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Redirecting to YouTube...</h2>
            <p className="text-slate-300">Please wait while we open the video</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20 shadow-2xl" data-testid="public-video-page">
        <CardHeader className="text-center pb-2">
          {/* YouTube-style play icon */}
          <div className="w-20 h-20 mx-auto bg-red-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-red-500/30">
            <Play className="w-10 h-10 text-white fill-white ml-1" />
          </div>
          <CardTitle className="text-2xl font-bold text-white mb-1">
            {video?.name}
          </CardTitle>
          <p className="text-slate-400 text-sm">
            {video?.committee_name}
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-4">
          <div className="text-center">
            <p className="text-slate-300 mb-6">
              Enter your ITS ID to watch the video
            </p>
            
            <div className="space-y-4">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter 8-digit ITS ID"
                value={itsId}
                onChange={handleItsChange}
                disabled={submitting}
                className="text-center text-2xl font-mono h-14 bg-white/10 border-white/30 text-white placeholder:text-slate-500 tracking-widest"
                maxLength={8}
                autoFocus
                data-testid="its-input"
              />
              
              {/* Progress indicator */}
              <div className="flex justify-center gap-1">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-1 rounded-full transition-colors ${
                      i < itsId.length ? 'bg-green-500' : 'bg-slate-600'
                    }`}
                  />
                ))}
              </div>
              
              <p className="text-xs text-slate-500">
                {itsId.length}/8 digits entered
              </p>
              
              <Button
                onClick={() => handleSubmit()}
                disabled={submitting || itsId.length !== 8}
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
                data-testid="watch-video-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Watch Video
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="text-center pt-4 border-t border-white/10">
            <p className="text-xs text-slate-500">
              Your viewing will be recorded for our records
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
